import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import LoginModal from './LoginModal';
import { User } from '../lib/types';

interface NavbarProps {
  user: User | null;
  onLoginSuccess: (user: User) => void;
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ user, onLoginSuccess, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
    setIsMenuOpen(false);
  };

  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      const headerOffset = 80;
      const elementPosition = pricingSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  const scrollToAgency = (e: React.MouseEvent) => {
    e.preventDefault();
    const agencySection = document.getElementById('agency-section');
    if (agencySection) {
      const headerOffset = 80;
      const elementPosition = agencySection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false);
    }
  };

  return (
    <nav 
      className="bg-white shadow-sm fixed w-full top-0 z-50"
      role="navigation"
      aria-label="Navigation principale"
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          <Logo />

          {/* Only show navigation menu for non-logged in users */}
          {!user && (
            <>
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-gray-700 hover:text-primary transition-colors">
                  Accueil
                </Link>
                <button
                  onClick={scrollToAgency}
                  className="text-gray-700 hover:text-primary transition-colors"
                >
                  Cabinets
                </button>
                <button
                  onClick={scrollToPricing}
                  className="text-gray-700 hover:text-primary transition-colors"
                >
                  Tarifs
                </button>
                <a href="#blog" className="text-gray-700 hover:text-primary transition-colors">
                  Blog
                </a>
                <button
                  onClick={handleLoginClick}
                  className="btn-primary"
                  aria-label="Commencer l'essai gratuit"
                >
                  Commencer
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-gray-700 hover:text-primary transition-colors p-2"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" aria-hidden="true" />
                ) : (
                  <Menu className="w-6 h-6" aria-hidden="true" />
                )}
              </button>
            </>
          )}

          {/* User Menu */}
          {user && (
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-gray-700 hover:text-primary transition-colors">
                Dashboard
              </Link>
              <button
                onClick={onLogout}
                className="text-gray-700 hover:text-red-600 transition-colors"
              >
                Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {!user && isMenuOpen && (
        <div 
          id="mobile-menu"
          className="md:hidden fixed inset-0 top-16 bg-white z-40"
          role="dialog"
          aria-modal="true"
          aria-label="Menu mobile"
        >
          <div className="flex flex-col p-6 space-y-6">
            <Link 
              to="/" 
              className="text-gray-700 hover:text-primary transition-colors text-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Accueil
            </Link>
            <button
              onClick={scrollToAgency}
              className="text-gray-700 hover:text-primary transition-colors text-lg text-left"
            >
              Cabinets
            </button>
            <button
              onClick={scrollToPricing}
              className="text-gray-700 hover:text-primary transition-colors text-lg text-left"
            >
              Tarifs
            </button>
            <a 
              href="#blog" 
              className="text-gray-700 hover:text-primary transition-colors text-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </a>
            <button
              onClick={() => {
                setIsMenuOpen(false);
                setIsLoginModalOpen(true);
              }}
              className="btn-primary w-full text-center py-3"
              aria-label="Commencer l'essai gratuit"
            >
              Essai gratuit
            </button>
          </div>
        </div>
      )}

      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={onLoginSuccess}
      />
    </nav>
  );
};

export default Navbar;